import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useApp } from "@/store/useApp";
import { hasPermission } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { getApiErrorMessage } from "@/lib/api";
import {
  archiveProcess,
  closeProcess,
  createProcess,
  fetchProcesses,
  newDraftVersion,
  openProcess,
  reopenProcess,
  type ProcessStatus,
  type ProcessSummary,
} from "@/lib/adminSelectionApi";

const STATUS_LABEL: Record<ProcessStatus, string> = {
  RASCUNHO: "Rascunho",
  ABERTO: "Aberto",
  ENCERRADO: "Encerrado",
  ARQUIVADO: "Arquivado",
};

function statusVariant(s: ProcessStatus): "default" | "secondary" | "outline" | "destructive" {
  switch (s) {
    case "ABERTO": return "default";
    case "RASCUNHO": return "secondary";
    case "ENCERRADO": return "outline";
    case "ARQUIVADO": return "destructive";
  }
}

export default function AdminSelection() {
  const navigate = useNavigate();
  const account = useApp((s) => s.account);
  const canCreate = hasPermission(account, "SELECAO.PROCESSO.CRIAR");
  const canOpen = hasPermission(account, "SELECAO.PROCESSO.ABRIR");
  const canClose = hasPermission(account, "SELECAO.PROCESSO.ENCERRAR");
  const canReopen = hasPermission(account, "SELECAO.PROCESSO.REABRIR");
  const canArchive = hasPermission(account, "SELECAO.PROCESSO.ARQUIVAR");
  const canNewVersion = hasPermission(account, "SELECAO.PROCESSO.NOVA_VERSAO");
  const [rows, setRows] = useState<ProcessSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", jobName: "", description: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await fetchProcesses());
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Falha ao carregar processos"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const submitNew = async () => {
    if (!form.name.trim()) return;
    setCreating(true);
    try {
      await createProcess({
        name: form.name.trim(),
        jobName: form.jobName.trim() || null,
        description: form.description.trim() || null,
      });
      toast.success("Processo criado (em rascunho)");
      setForm({ name: "", jobName: "", description: "" });
      setShowNew(false);
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Não foi possível criar o processo"));
    } finally {
      setCreating(false);
    }
  };

  const run = async (id: string, action: () => Promise<unknown>, ok: string) => {
    setBusy(id);
    try {
      await action();
      toast.success(ok);
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Não foi possível concluir a ação"));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="container mx-auto max-w-6xl px-4 py-6">
      <div className="mb-6 flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Processo Seletivo</h1>
          <p className="text-sm text-muted-foreground">Processos seletivos configuráveis do NatureXpress.</p>
        </div>
        {canCreate && (
          <Button onClick={() => setShowNew((v) => !v)}>{showNew ? "Cancelar" : "+ Novo processo"}</Button>
        )}
      </div>

      {showNew && canCreate && (
        <div className="mb-6 rounded-xl border border-border bg-surface-elevated p-4">
          <h2 className="text-sm font-semibold">Novo processo seletivo</h2>
          <div className="mt-3 grid gap-3">
            <div>
              <label className="text-xs text-muted-foreground">Nome do processo *</label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Ex.: Assistente de Logística — 2026" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Nome da vaga</label>
              <Input value={form.jobName} onChange={(e) => setForm((f) => ({ ...f, jobName: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Descrição</label>
              <Textarea rows={3} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setShowNew(false)}>Cancelar</Button>
              <Button onClick={() => void submitNew()} disabled={creating || !form.name.trim()}>
                {creating ? "Criando…" : "Criar processo"}
              </Button>
            </div>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            O processo é criado em rascunho e vazio. Use Nova versão/Configurações para montar etapas, perguntas e critérios.
          </p>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">Carregando…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nenhum processo cadastrado.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 text-left">
              <tr>
                <th className="px-3 py-2">Processo</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Versão</th>
                <th className="px-3 py-2">Candidatos</th>
                <th className="px-3 py-2">Criado</th>
                <th className="px-3 py-2 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} className="border-t border-border align-top">
                  <td className="px-3 py-3">
                    <div className="font-medium">{p.name}</div>
                    {p.jobName && <div className="text-xs text-muted-foreground">{p.jobName}</div>}
                  </td>
                  <td className="px-3 py-3"><Badge variant={statusVariant(p.status)}>{STATUS_LABEL[p.status]}</Badge></td>
                  <td className="px-3 py-3 font-mono">v{p.activeVersionNumber}</td>
                  <td className="px-3 py-3 font-mono">{p.candidateCount}</td>
                  <td className="px-3 py-3">{new Date(p.createdAt).toLocaleDateString("pt-BR")}</td>
                  <td className="px-3 py-3">
                    <div className="flex flex-wrap justify-end gap-2">
                      {p.status === "RASCUNHO" && canOpen && (
                        <Button size="sm" disabled={busy === p.id}
                          onClick={() => run(p.id, () => openProcess(p.id), "Processo aberto")}>Abrir</Button>
                      )}
                      {p.status === "ABERTO" && canClose && (
                        <Button size="sm" variant="outline" disabled={busy === p.id}
                          onClick={() => run(p.id, () => closeProcess(p.id), "Processo encerrado")}>Encerrar</Button>
                      )}
                      {(p.status === "ENCERRADO" || p.status === "ARQUIVADO") && canReopen && (
                        <Button size="sm" variant="outline" disabled={busy === p.id}
                          onClick={() => run(p.id, () => reopenProcess(p.id), "Processo reaberto")}>Reabrir</Button>
                      )}
                      {p.status !== "ARQUIVADO" && canArchive && (
                        <Button size="sm" variant="ghost" disabled={busy === p.id}
                          onClick={() => run(p.id, () => archiveProcess(p.id), "Processo arquivado")}>Arquivar</Button>
                      )}
                      {canNewVersion && (
                        <Button size="sm" variant="ghost" disabled={busy === p.id}
                          onClick={() => run(p.id, () => newDraftVersion(p.id), "Nova versão criada")}>Nova versão</Button>
                      )}
                      <Button size="sm" variant="secondary"
                        onClick={() => navigate(`/admin/selecao/${p.id}/candidatos`)}>Candidatos</Button>
                      <Button size="sm" variant="secondary"
                        onClick={() => navigate(`/admin/selecao/${p.id}/ranking`)}>Ranking</Button>
                      <Button size="sm" variant="ghost"
                        onClick={() => navigate(`/admin/selecao/${p.id}/ajustes`)}>Configurações</Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
